/**
 * @file   src\Pages\RevenueReports.tsx
 * @brief  Revenue Reports page from header.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import { Container, Table, Breadcrumb, useEffect, useOutletContext, Dropdown, useState, Button, useParams, Pagination, React } from '../utils/thirdpartyComponents';
import { ItemTypeEnum } from '../utils/enums';
import { formValueInterface, RevenueRecord } from '../interfaces/generalInterface';
import { AppContextType } from '../interfaces/generalInterface';
import DatePicker from 'react-datepicker';
import FilterIcon from '../assets/images/icons/filter';
import CWRadio from '../components/CWRadio';
import { ChangeEvent } from 'react';
import axios from '../config/axiosConfig';
import { CompanyApi, RevenueApi } from '../utils/apiUrls';
import SmallLoader from '../components/SmallLoader';
import moment from "moment-timezone"

const RevenueReportsSuperAdmin = () => {

    // Access the context
    const { updateData } = useOutletContext<AppContextType>();
    const [filterClass, setFilterClass] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedOption, setSelectedOption] = useState('month');
    const [temporaryOption, setTemporaryOption] = useState(selectedOption)
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const params = useParams();
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [washRecords, setWashRecords] = useState<RevenueRecord[]>([])
    const today = new Date();

    const [formValues, setFormValues] = useState<formValueInterface>({
        month: today.getMonth() + 1,
        year: today.getFullYear(),
        fromDate: null,
        toDate: null,
        dateFilterType: 'month',
        defaultFilter: true,
        dateFilterChanged: false,
    })

    // State to company details
    const [company, setCompany] = useState({
        companyName: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        zipCode: ""
    });

    const setDropdown = () => {
        setTemporaryOption(selectedOption); // Reset temporary selection to confirmed option
        setShowDropdown(!showDropdown)
    }

    // To display heading
    useEffect(() => {
        getCompanyAddress();

    }, [])

    useEffect(() => {
        handleUpdate()
    }, [company])

    useEffect(() => {
        getRecords(currentPage)
    }, [formValues, currentPage])

    const getCompanyAddress = async () => {
        try {
            const id = params.id ? parseInt(params.id) : 0;
            const { data } = await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id)
            if (data?.error === 0) {
                setCompany(data.data);
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Update the data
    const handleUpdate = () => {
        updateData({ heading: "Revenue Reports", url: "", showBackButton: false, showMainMenu: true, activeLink: "reports" });
    };



    const getRecords = async (pageNo = 1) => {
        try {

            setLoading(true)
            let param;
            param = `?pageNo=${pageNo}`

            // Need to be considered only if filter by is done.
            if (formValues.filterChanged) {
                if (formValues.month && formValues.dateFilterType === 'month') {
                    param += "&month=" + formValues.month;
                    param += "&year=" + new Date().getFullYear();
                }
                if (formValues.year && formValues.dateFilterType === 'year') {
                    param += "&year=" + formValues.year;
                }
                if (formValues.fromDate && formValues.dateFilterType === 'custom') {
                    param += "&fromDate=" + formValues.fromDate;
                }
                if (formValues.toDate && formValues.dateFilterType === 'custom') {
                    param += "&toDate=" + formValues.toDate;
                }
            } else {
                if (formValues.month && formValues.dateFilterType === 'month') {
                    param += "&month=" + formValues.month;
                    param += "&year=" + new Date().getFullYear();
                }
            }

            const { data } = await axios.get(RevenueApi.SUPERADMIN_REVENUE_REPORT + param)
            if (data?.error === 0 && Array.isArray(data?.data?.list)) {
                const revenue_list = data.data.list;
                if (revenue_list.length > 0) {
                    setWashRecords(revenue_list)
                    setTotalPages(Math.ceil(data.data.totalCount / data.data.limit) || 1);
                } else {
                    console.log('Revenue list is empty');
                    setWashRecords([]);
                }
            } else {
                console.log('Revenue list is not available or is not an array');
                setWashRecords([]);
            }
            setLoading(false)
        } catch (error: any) {
            console.error('Error fetching revenue list:', error.message);
            setLoading(false)
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    // Handle radio button change
    const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
        setTemporaryOption(event.target.id); // Update temporary selection
    };

    // Handle DatePicker changes
    const handleDateChange = (setter: React.Dispatch<React.SetStateAction<Date | null>>) => (date: Date | null) => {
        setter(date);
    };


    const getVisiblePageNumbers = () => {
        const totalPageNumbersToShow = 5;
        let startPage = Math.max(1, currentPage - Math.floor(totalPageNumbersToShow / 2));
        let endPage = startPage + totalPageNumbersToShow - 1;

        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - totalPageNumbersToShow + 1);
        }

        const pageNumbers = [];
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }

        return pageNumbers;
    };

    const handlePageChange = (newPage: number) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };


    //To clear form
    const handleClear = async () => {
        setTemporaryOption('month'); // Reset temporary selection
        setSelectedOption('month');
        setStartDate(null);
        setEndDate(null);
        const today = new Date();
        setFormValues({
            month: today.getMonth() + 1,
            year: today.getFullYear(),
            fromDate: null,
            toDate: null,
            dateFilterType: 'month',
            defaultFilter: true,
            dateFilterChanged: false,
        });
        await setCurrentPage(1)
        setDropdown();
        setFilterClass('')

    }

    // Handle form submission
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault(); // Prevent the default form submission behavior
        setSelectedOption(temporaryOption); // Confirm the selection

        let month = null;
        let year = null
        const today = new Date();
        if (temporaryOption === 'month') {
            month = today.getMonth() + 1
        } else {
            year = today.getFullYear()
        }
        let filterChanged = false;
        let dateFilterChanged = false;


        if (temporaryOption !== 'month') {
            filterChanged = true;
            dateFilterChanged = true;
        }

        if (filterChanged === true) {
            setFilterClass('filtered')
        } else {
            setFilterClass('')
        }
        let fromDate = null;
        if (startDate) {
            fromDate = moment(startDate).format('YYYY-MM-DD')
        }
        let toDate = null;
        if (endDate) {
            toDate = moment(endDate).format('YYYY-MM-DD')
        }
        // Get form values
        setFormValues({
            month: month,
            year: year,
            fromDate: fromDate,
            toDate: toDate,
            dateFilterType: temporaryOption,
            filterChanged: filterChanged,
            defaultFilter: (!filterChanged) ? true : false,
            dateFilterChanged: dateFilterChanged,
        });

        setCurrentPage(1)
        setDropdown()

    };

    const formatDate = (createdDate: string) => {
        const date = moment(createdDate);
        const formattedDate = date.format('MM/DD/YYYY');
        return formattedDate
    }

    function capitalizeFirstLetter(criteria: string) {
        return criteria.charAt(0).toUpperCase() + criteria.slice(1);
    }

    return (

        <div className='manage-carwash-container order-list-container'>
            <Container>
                <Breadcrumb>
                    <Breadcrumb.Item active>Revenue Reports</Breadcrumb.Item>
                </Breadcrumb>

                <div className="manage-header-block d-flex justify-content-between align-items-center">
                    <div className="search-wrap w-100">
                        <div className="result-group d-flex align-items-center">

                            <p className='title f-bold'>Results for</p>
                            <Dropdown className={"filter-dropdown " + filterClass} align="start" show={showDropdown} onToggle={setDropdown}>
                                <Dropdown.Toggle id="dropdown-autoclose-true" onClick={setDropdown}>
                                    Filter by
                                    <FilterIcon />
                                    {formValues.dateFilterType && capitalizeFirstLetter(formValues.dateFilterType)}
                                </Dropdown.Toggle>

                                <Dropdown.Menu>
                                    <h6>Filter by</h6>
                                    <form onSubmit={handleSubmit}>

                                        <div className="radio-field-group d-flex flex-column">
                                            <CWRadio
                                                id="month"
                                                name="timeframe"
                                                label="Current Month"
                                                value="month"
                                                checked={temporaryOption === 'month'}
                                                onChange={handleRadioChange}
                                            />
                                            <CWRadio
                                                id="year"
                                                name="timeframe"
                                                label="Current Year"
                                                value="year"
                                                checked={temporaryOption === 'year'}
                                                onChange={handleRadioChange}
                                            />
                                            <CWRadio
                                                id="custom"
                                                name="timeframe"
                                                label="Custom"
                                                value="custom"
                                                checked={temporaryOption === 'custom'}
                                                onChange={handleRadioChange}
                                            />
                                        </div>
                                        {temporaryOption === "custom" ? (<div className="calendar-field d-flex flex-column">
                                            <DatePicker
                                                showIcon
                                                selected={startDate}
                                                onChange={handleDateChange(setStartDate)}
                                                placeholderText='From'
                                            />
                                            <DatePicker
                                                showIcon
                                                selected={endDate}
                                                onChange={handleDateChange(setEndDate)}
                                                placeholderText='To'
                                                minDate={startDate || undefined}

                                            />
                                        </div>) : null}

                                        <div className="button-block d-flex justify-content-between">
                                            <Button title="Reset" variant="light" className="light-btn" onClick={() => handleClear()}>Reset</Button>
                                            <Button title="Apply" variant="primary" className="primary-btn" type="submit">Apply</Button>
                                        </div>
                                    </form>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                </div>
                <div className="cw-table-wrap">
                    <Table responsive className='m-0'>
                        <thead>
                            <tr>
                                <th>Company Name</th>
                                <th>Price</th>
                                <th>Transaction Date</th>
                                <th>Transaction For</th>
                                {/* <th>Wash Name</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {(washRecords).length > 0 ?
                                (washRecords).map((record, index) => (
                                    <tr key={index}>
                                        <td width="35%">{record.CompanyName}</td>
                                        <td>${record.Price}</td>
                                        <td>{formatDate(record.TransDate)}</td>
                                        {/* <td>{record.WashName}</td> */}
                                        <td>{ItemTypeEnum[record.ItemType as keyof typeof ItemTypeEnum] || 'Unknown Type'}</td>                                    </tr>
                                )) :
                                (
                                    <tr>
                                        <td style={{ textAlign: 'center' }} colSpan={7} >No data found.</td>
                                    </tr>
                                )}
                        </tbody>
                    </Table>

                    {washRecords.length ? <div className="mt-3 d-flex justify-content-end">
                        <Pagination>
                            <Pagination.First
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(1)}
                            />
                            <Pagination.Prev
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            />
                            {getVisiblePageNumbers().map((pageNumber) => (
                                <Pagination.Item
                                    key={pageNumber}
                                    active={pageNumber === currentPage}
                                    onClick={() => handlePageChange(pageNumber)}
                                >
                                    {pageNumber}
                                </Pagination.Item>
                            ))}
                            <Pagination.Next
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            />
                            <Pagination.Last
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(totalPages)}
                            />
                        </Pagination>
                    </div> : null}
                </div>
            </Container >

            {loading && <SmallLoader />}
        </div >
    )
}

export default RevenueReportsSuperAdmin;
